import { Info, ListChecks, FileText } from 'lucide-react'
import { Streamdown } from 'streamdown'
import { Badge } from '@/components/ui/badge'
import type { ScoringCriteria } from '@deepguide-ai/types'

type VersionedScoringCriteria = {
  version: number
  scoringCriteria: ScoringCriteria
}

type EvidenceTopic = {
  id: string
  title: string
  docId: string | null
}

interface TestDetailsTabProps {
  scoringCriteria: ScoringCriteria | null
  versionedScoringCriteria?: VersionedScoringCriteria | null
  isAdHoc?: boolean
  evidenceTopic?: EvidenceTopic | null
}

export function TestDetailsTab({
  scoringCriteria,
  versionedScoringCriteria,
  isAdHoc = false,
  evidenceTopic,
}: TestDetailsTabProps) {
  // Ad-hoc test details
  if (isAdHoc) {
    return (
      <div className='space-y-8'>
        {/* Ad-hoc Evidence Info */}
        <div className='space-y-3'>
          <h4 className='text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase'>
            <FileText className='h-3 w-3' />
            About This Evidence
          </h4>
          <div className='bg-muted/30 rounded-lg border p-4'>
            <p className='text-sm'>
              This is an <strong>ad-hoc evidence submission</strong> — manually recorded evidence that was submitted without a predefined test template.
            </p>
            <p className='text-muted-foreground mt-2 text-sm'>
              Ad-hoc evidence is useful for capturing one-time compliance activities, responding to audit requests, or documenting processes that don't have a recurring test workflow.
            </p>
          </div>
        </div>

        {/* Evidence Topic Info */}
        {/* {evidenceTopic && (
          <div className='space-y-3'>
            <h4 className='text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase'>
              <Info className='h-3 w-3' />
              Evidence Details
            </h4>
            <div className='space-y-3 text-sm'>
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Evidence Title</span>
                <span className='font-medium'>{evidenceTopic.title}</span>
              </div>
            </div>
          </div>
        )} */}
      </div>
    )
  }

  // Regular test - empty state
  if (
    !scoringCriteria?.instructions &&
    (!scoringCriteria?.requiredElements ||
      scoringCriteria.requiredElements.length === 0)
  ) {
    return (
      <div className='bg-muted/30 rounded-lg border py-8 text-center'>
        <Info className='text-muted-foreground mx-auto h-10 w-10 opacity-50' />
        <p className='text-muted-foreground mt-3 text-sm'>
          No details configured for this test
        </p>
      </div>
    )
  }

  // Regular test with scoring criteria
  return (
    <div className='space-y-8'>
      {/* Instructions */}
      {scoringCriteria?.instructions && (
        <div className='space-y-3'>
          <h4 className='text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase'>
            <Info className='h-3 w-3' />
            Instructions
            {/* {versionedScoringCriteria && (
              <Badge variant='outline' className='ml-1 text-[10px]'>
                v{versionedScoringCriteria.version}
              </Badge>
            )} */}
          </h4>
          <div className='bg-muted/30 rounded-lg border p-4'>
            <div className='prose prose-sm dark:prose-invert max-w-none text-sm'>
              <Streamdown>{scoringCriteria.instructions}</Streamdown>
            </div>
          </div>
        </div>
      )}

      {/* Expected Outcomes */}
      {scoringCriteria?.requiredElements &&
        scoringCriteria.requiredElements.length > 0 && (
          <div className='space-y-3'>
            <h4 className='text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase'>
              <ListChecks className='h-3 w-3' />
              Expected Outcomes
              {/* {versionedScoringCriteria && (
                <Badge variant='outline' className='ml-1 text-[10px]'>
                  v{versionedScoringCriteria.version}
                </Badge>
              )} */}
            </h4>
            <div className='space-y-2'>
              {scoringCriteria.requiredElements.map((element, index) => (
                <div
                  key={index}
                  className='bg-muted/30 rounded-lg border p-3'
                >
                  <div className='flex items-center justify-between'>
                    <span className='font-medium'>{element.name}</span>
                    {element.required && (
                      <Badge variant='secondary' className='text-xs'>
                        Required
                      </Badge>
                    )}
                  </div>
                  {element.description && (
                    <p className='text-muted-foreground mt-1 text-sm'>
                      {element.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  )
}
